import { useState } from 'react'
import { DismissibleTooltip } from '../../components/common/DismissibleTooltip'
import { getConfidenceStatus, getConfidenceColor, CONFIDENCE_LABELS } from './confidenceUtils'
import { BadgeWrapper, SpinnerGroup } from './ConfidenceBadge.styles'
import { ConfidenceDetailModal } from './ConfidenceDetailModal'
import type { ConfidenceScoreEntry } from './confidenceScoresStore'

interface ConfidenceBadgeProps {
  /** 0–100 confidence score from research. undefined = untested. */
  score: number | undefined
  /** True while the backend is actively computing a score for this node. */
  isPending?: boolean
  /** Required to open the evidence modal on click. */
  nodeId?: string
  nodeLabel?: string
  mindmapId?: string
  /** Full score entry from store (includes dimensions + reasoning). */
  scoreEntry?: ConfidenceScoreEntry
}

const SIZE = 26
const STROKE_WIDTH = 3
const RADIUS = (SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const PENDING_COLOR = '#6366f1'

// Indeterminate spinner: a ~28% arc rotating around the ring. 28% is the
// sweet spot at this radius — long enough to read as motion, short enough
// that the gap is clearly visible so the animation never looks like a full
// stalled ring.
const SPINNER_ARC = CIRCUMFERENCE * 0.28
const SPINNER_GAP = CIRCUMFERENCE - SPINNER_ARC

export function ConfidenceBadge({
  score,
  isPending = false,
  nodeId,
  nodeLabel,
  mindmapId,
  scoreEntry,
}: ConfidenceBadgeProps) {
  const [modalOpen, setModalOpen] = useState(false)

  const status = getConfidenceStatus(score)
  const color = isPending ? PENDING_COLOR : getConfidenceColor(score)
  const isUntested = status === 'untested'

  const arcLength = isUntested ? 0 : (score! / 100) * CIRCUMFERENCE
  const gap = CIRCUMFERENCE - arcLength

  const tooltipLabel = isPending ? 'Analysing research…' : CONFIDENCE_LABELS[status]
  const centerLabel = isUntested ? '?' : String(score)
  const fontSize = isUntested ? 9 : score! >= 100 ? 6 : 8

  const isClickable = !isPending && score !== undefined && !!scoreEntry && !!nodeId && !!mindmapId

  return (
    <>
      <BadgeWrapper
        className="nodrag"
        onClick={isClickable ? () => setModalOpen(true) : undefined}
        style={isClickable ? { cursor: 'pointer' } : undefined}
      >
        <DismissibleTooltip
          title={
            isClickable
              ? `Confidence: ${tooltipLabel} — click for details`
              : `Confidence: ${tooltipLabel}`
          }
          placement="top"
          mouseEnterDelay={0.3}
        >
          <svg
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block', flexShrink: 0 }}
            role={isPending ? 'progressbar' : undefined}
            aria-label={isPending ? 'Analysing research' : undefined}
          >
            {/* Track ring — kept faint while pending so the spinner has a rail */}
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              opacity={isPending ? 0.15 : 0.2}
            />
            {isPending ? (
              <SpinnerGroup>
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  stroke={color}
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={`${SPINNER_ARC} ${SPINNER_GAP}`}
                  strokeDashoffset={CIRCUMFERENCE / 4}
                  strokeLinecap="round"
                />
              </SpinnerGroup>
            ) : (
              <>
                {!isUntested && (
                  <circle
                    cx={SIZE / 2}
                    cy={SIZE / 2}
                    r={RADIUS}
                    stroke={color}
                    strokeWidth={STROKE_WIDTH}
                    strokeDasharray={`${arcLength} ${gap}`}
                    strokeDashoffset={CIRCUMFERENCE / 4}
                    strokeLinecap="round"
                  />
                )}
                <text
                  x={SIZE / 2}
                  y={SIZE / 2}
                  fontSize={fontSize}
                  fill={color}
                  fontWeight="600"
                  dominantBaseline="central"
                  textAnchor="middle"
                  style={{ fontFamily: 'inherit', userSelect: 'none' }}
                >
                  {centerLabel}
                </text>
              </>
            )}
          </svg>
        </DismissibleTooltip>
      </BadgeWrapper>

      {isClickable && scoreEntry && nodeId && nodeLabel && mindmapId && (
        <ConfidenceDetailModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          nodeLabel={nodeLabel}
          mindmapId={mindmapId}
          nodeId={nodeId}
          scoreEntry={scoreEntry}
        />
      )}
    </>
  )
}
